/**
 * useAnalytics
 *
 * Fetches all 4 analytics reports independently so each card can show
 * its own loading state as responses arrive.
 *
 * Returns:
 *   loading              - true while ANY request is still in flight
 *   loadingScans         - true while scansOverTime is in flight
 *   loadingTopQr         - true while topQrCodes is in flight
 *   loadingDeviceBrowser - true while deviceBrowserBreakdown is in flight
 *   loadingCreators      - true while qrCodeCreators is in flight
 *   error        - Error object if a critical error occurred
 *   scansOverTime  - [{ date, scans, visits }]
 *   topQrCodes     - [{ key, qrName, scans, visits }]
 *   devices        - [{ key, label, scans, pct }]
 *   browsers       - [{ key, label, scans, pct }]
 *   qrCreators     - [{ key, creator, codesCreated, topScans }]
 *   summaryStats   - derived from scansOverTime + topQrCodes
 *   refetch        - call to re-trigger all requests
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchReport, REPORT_TYPES } from '../api/analyticsApi';

/* ── Data mappers ── */

function formatDate(raw) {
  try {
    return new Date(raw).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return raw;
  }
}

function shortEmail(email = '') {
  return email.split('@')[0] || email;
}

function mapScansOverTime(rawList = []) {
  return rawList.map((d) => ({
    date:   formatDate(d.date),
    scans:  Number(d.scans) || 0,
    visits: Number(d.visits) || 0,
  }));
}

function mapTopQrCodes(rawList = []) {
  return rawList.map((d, i) => ({
    key:    d.qr_slug || i,
    qrName: d.qr_slug,
    scans:  Number(d.total_scans)  || 0,
    visits: Number(d.total_visits) || 0,
    status: d.status,
  }));
}

function mapDeviceBrowser(raw = {}) {
  const devices = (raw.devices || []).map((d, i) => ({
    key:   i,
    label: d.device_type,
    scans: Number(d.scans) || 0,
    pct:   parseFloat(d.pct) || 0,
  }));
  const browsers = (raw.browsers || []).map((d, i) => ({
    key:   i,
    label: d.browser,
    scans: Number(d.scans) || 0,
    pct:   parseFloat(d.pct) || 0,
  }));
  return { devices, browsers };
}

function mapQrCreators(rawList = []) {
  return rawList.map((d, i) => ({
    key:          d.hs_user_id || i,
    creator:      shortEmail(d.hs_user_email),
    codesCreated: Number(d.codes_created)  || 0,
    topScans:     Number(d.top_qr_scans)   || 0,
    topSlug:      d.top_qr_slug,
    email:        d.hs_user_email,
  }));
}

function deriveSummary(scansOverTime, topQrCodes) {
  const totalScans    = scansOverTime.reduce((s, d) => s + d.scans,  0);
  const totalVisits   = scansOverTime.reduce((s, d) => s + d.visits, 0);
  const qrCodesActive = topQrCodes.length;
  return { totalScans, totalVisits, qrCodesActive };
}

/* ── Hook ── */
export function useAnalytics(range) {
  const [loadingScans,         setLoadingScans]         = useState(true);
  const [loadingTopQr,         setLoadingTopQr]         = useState(true);
  const [loadingDeviceBrowser, setLoadingDeviceBrowser] = useState(true);
  const [loadingCreators,      setLoadingCreators]      = useState(true);

  const [error, setError] = useState(null);
  const [tick, setTick]   = useState(0);

  const [scansOverTime, setScansOverTime] = useState([]);
  const [topQrCodes,    setTopQrCodes]    = useState([]);
  const [devices,       setDevices]       = useState([]);
  const [browsers,      setBrowsers]      = useState([]);
  const [qrCreators,    setQrCreators]    = useState([]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;

    setError(null);
    setLoadingScans(true);
    setLoadingTopQr(true);
    setLoadingDeviceBrowser(true);
    setLoadingCreators(true);

    /* Each report fetches independently and settles its own loading state */

    fetchReport(REPORT_TYPES.SCANS_OVER_TIME, range)
      .then(({ data }) => {
        if (cancelled) return;
        setScansOverTime(mapScansOverTime(data));
      })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoadingScans(false); });

    fetchReport(REPORT_TYPES.TOP_QR_CODES, range)
      .then(({ data }) => {
        if (cancelled) return;
        setTopQrCodes(mapTopQrCodes(data));
      })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoadingTopQr(false); });

    fetchReport(REPORT_TYPES.DEVICE_BROWSER, range)
      .then(({ data }) => {
        if (cancelled) return;
        const { devices: d, browsers: b } = mapDeviceBrowser(data);
        setDevices(d);
        setBrowsers(b);
      })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoadingDeviceBrowser(false); });

    fetchReport(REPORT_TYPES.QR_CREATORS, range)
      .then(({ data }) => {
        if (cancelled) return;
        setQrCreators(mapQrCreators(data));
      })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoadingCreators(false); });

    return () => { cancelled = true; };
  }, [range, tick]);

  const loading = loadingScans || loadingTopQr || loadingDeviceBrowser || loadingCreators;
  const summaryStats = deriveSummary(scansOverTime, topQrCodes);

  return {
    /* individual loading flags */
    loadingScans,
    loadingTopQr,
    loadingDeviceBrowser,
    loadingCreators,
    /* overall */
    loading,
    error,
    refetch,
    /* data */
    scansOverTime,
    topQrCodes,
    devices,
    browsers,
    qrCreators,
    summaryStats,
  };
}
